import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tooltip,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Settings as SettingsIcon,
  Mic as MicIcon,
  FavoriteBorder as FavoriteBorderIcon,
  FolderOutlined as FolderIcon,
  Inbox as InboxIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  ChatBubbleRounded as ChatBubbleRoundedIcon,
  Chat as ChatIcon,
  Message as MessageIcon,
  Forum as ForumIcon,
} from '@mui/icons-material';
import { Header } from '../components/Header';
import { AdminBottomNavigation } from '../components/AdminBottomNavigation';
import { Pagination } from '../components/Pagination';
import { useAuth } from '../hooks/useAuth';
import { useCompany } from '../hooks/useAuth';
import { PostService } from '../services/postService';
import { AuthService } from '../services/authService';
import { Post } from '../types/index';
import { AppColors } from '../theme/colors';
import { AdminSidebar } from '../components/AdminSidebar';
import { AdminDesktopSidebar } from '../components/AdminDesktopSidebar';
import { useIsDesktop } from '../hooks/useMediaQuery';
import {
  collection,
  query as fsQuery,
  where,
  orderBy,
  limit as fsLimit,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import { getBoxTemplate } from '../constants/industryTemplates';
import { ensureAccessLink } from '../services/accessLinkService';
import { buildAccessLinkMessage, saveAccessLinkPoster } from '../utils/accessLinkPoster';
import { QRCodeSVG } from 'qrcode.react';

type AdminTab = 'voice' | 'thanks' | 'theme';

export const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDesktop = useIsDesktop();
  
  const { user, loading: authLoading } = useAuth();
  const { company, loading: companyLoading } = useCompany(user?.uid || null);
  
  const [drawerOpen, setDrawerOpen] = useState(false);
  // URLパラメータからタブを取得、なければ'voice'をデフォルトに
  const initialTab = (searchParams.get('tab') as AdminTab) || 'voice';
  const [activeTab, setActiveTab] = useState<AdminTab>(initialTab);
  const [posts, setPosts] = useState<Post[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);
  
  // 切り替え用の状態
  const [isSwitching, setIsSwitching] = useState(false);

  // voiceタブ専用の状態
  const [voicePosts, setVoicePosts] = useState<Post[]>([]);
  const [voiceCommentCounts, setVoiceCommentCounts] = useState<Record<string, number>>({});
  const [keyword, setKeyword] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<
    'accepted' | 'in_progress' | 'completed' | 'pending' | null
  >(null);
  const [expandedPostMap, setExpandedPostMap] = useState<Record<string, boolean>>({});
  const [voiceClampedMap, setVoiceClampedMap] = useState<Record<string, boolean>>({});
  const [statusChangeDialog, setStatusChangeDialog] = useState<{ open: boolean; post: Post | null }>({ open: false, post: null });
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [tempKeyword, setTempKeyword] = useState('');
  const [tempDate, setTempDate] = useState<Date | null>(null);
  
  // 登録情報ダイアログ
  const [registrationInfoDialog, setRegistrationInfoDialog] = useState(false);
  // メールアドレスとパスワードは本人が登録フォームで入力した値のため、ここでは表示しない
  const [registrationInfo, setRegistrationInfo] = useState<{
    corporateCode: string;
    isNewRegistration?: boolean;
    freeTrialEndDate?: string;
  } | null>(null);
  // 投稿用のQRコード・URL（登録完了時に用意する）
  const [accessLinkUrl, setAccessLinkUrl] = useState<string | null>(null);
  const [accessLinkNotice, setAccessLinkNotice] = useState<string | null>(null);

  // ページネーション用
  const ITEMS_PER_PAGE = 10;
  
  // URLパラメータからページ番号を取得（voiceタブ用）
  const voiceCurrentPage = useMemo(() => {
    if (activeTab === 'voice') {
      const pageParam = searchParams.get('voicePage');
      return pageParam ? parseInt(pageParam, 10) : 1;
    }
    return 1;
  }, [searchParams, activeTab]);
  
  // URLパラメータからページ番号を取得（thanksタブ用）
  const thanksCurrentPage = useMemo(() => {
    if (activeTab === 'thanks') {
      const pageParam = searchParams.get('thanksPage');
      return pageParam ? parseInt(pageParam, 10) : 1;
    }
    return 1;
  }, [searchParams, activeTab]);
  
  // URLパラメータからページ番号を取得（themeタブ用）
  const themeCurrentPage = useMemo(() => {
    if (activeTab === 'theme') {
      const pageParam = searchParams.get('themePage');
      return pageParam ? parseInt(pageParam, 10) : 1;
    }
    return 1;
  }, [searchParams, activeTab]);
  
  // ページ番号を変更する関数（voiceタブ用）
  const setVoiceCurrentPage = useCallback((page: number) => {
    if (activeTab === 'voice') {
      const params = new URLSearchParams(searchParams);
      if (page === 1) {
        params.delete('voicePage');
      } else {
        params.set('voicePage', page.toString());
      }
      params.set('tab', 'voice');
      navigate(`/admin?${params.toString()}`, { replace: true });
    }
  }, [activeTab, searchParams, navigate]);
  
  // ページ番号を変更する関数（thanksタブ用）
  const setThanksCurrentPage = useCallback((page: number) => {
    if (activeTab === 'thanks') {
      const params = new URLSearchParams(searchParams);
      if (page === 1) {
        params.delete('thanksPage');
      } else {
        params.set('thanksPage', page.toString());
      }
      params.set('tab', 'thanks');
      navigate(`/admin?${params.toString()}`, { replace: true });
    }
  }, [activeTab, searchParams, navigate]);
  
  // ページ番号を変更する関数（themeタブ用）
  const setThemeCurrentPage = useCallback((page: number) => {
    if (activeTab === 'theme') {
      const params = new URLSearchParams(searchParams);
      if (page === 1) {
        params.delete('themePage');
      } else {
        params.set('themePage', page.toString());
      }
      params.set('tab', 'theme');
      navigate(`/admin?${params.toString()}`, { replace: true });
    }
  }, [activeTab, searchParams, navigate]);
  
  // フィルタ変更時にページをリセット（voiceタブ用）
  const prevVoiceFiltersRef = useRef({ selectedDate, keyword, selectedStatus });
  const isVoiceInitialMountRef = useRef(true);
  useEffect(() => {
    if (activeTab === 'voice') {
      // 初回マウント時はスキップ
      if (isVoiceInitialMountRef.current) {
        isVoiceInitialMountRef.current = false;
        prevVoiceFiltersRef.current = { selectedDate, keyword, selectedStatus };
        return;
      }
      
      const prevFilters = prevVoiceFiltersRef.current;
      const filtersChanged = 
        prevFilters.selectedDate !== selectedDate ||
        prevFilters.keyword !== keyword ||
        prevFilters.selectedStatus !== selectedStatus;
      
      if (filtersChanged) {
        const currentPage = searchParams.get('voicePage');
        if (currentPage && currentPage !== '1') {
          const params = new URLSearchParams(searchParams);
          params.delete('voicePage');
          params.set('tab', 'voice');
          navigate(`/admin?${params.toString()}`, { replace: true });
        }
      }
      
      prevVoiceFiltersRef.current = { selectedDate, keyword, selectedStatus };
    }
  }, [selectedDate, keyword, selectedStatus, activeTab, searchParams, navigate]);

  // thanksタブ専用の状態
  const [thanksPosts, setThanksPosts] = useState<Post[]>([]);
  const [thanksKeyword, setThanksKeyword] = useState('');
  const [thanksSelectedDate, setThanksSelectedDate] = useState<Date | null>(null);
  const [thanksVisibilityFilter, setThanksVisibilityFilter] = useState<'approved' | 'pending' | null>(null);
  const [thanksExpandedMap, setThanksExpandedMap] = useState<Record<string, boolean>>({});
  const [thanksClampedMap, setThanksClampedMap] = useState<Record<string, boolean>>({});
  const [thanksSearchDialogOpen, setThanksSearchDialogOpen] = useState(false);
  const [thanksTempKeyword, setThanksTempKeyword] = useState('');
  const [thanksTempDate, setThanksTempDate] = useState<Date | null>(null);

  // フィルタ変更時にページをリセット（thanksタブ用）
  const prevThanksFiltersRef = useRef<{ thanksSelectedDate: Date | null; thanksKeyword: string } | null>(null);
  const isThanksInitialMountRef = useRef(true);
  useEffect(() => {
    if (activeTab === 'thanks') {
      // 初回マウント時はスキップ
      if (isThanksInitialMountRef.current) {
        isThanksInitialMountRef.current = false;
        prevThanksFiltersRef.current = { thanksSelectedDate, thanksKeyword };
        return;
      }
      
      const prevFilters = prevThanksFiltersRef.current;
      if (!prevFilters) {
        prevThanksFiltersRef.current = { thanksSelectedDate, thanksKeyword };
        return;
      }
      
      const filtersChanged = 
        prevFilters.thanksSelectedDate !== thanksSelectedDate ||
        prevFilters.thanksKeyword !== thanksKeyword;
      
      if (filtersChanged) {
        const currentPage = searchParams.get('thanksPage');
        if (currentPage && currentPage !== '1') {
          const params = new URLSearchParams(searchParams);
          params.delete('thanksPage');
          params.set('tab', 'thanks');
          navigate(`/admin?${params.toString()}`, { replace: true });
        }
      }
      
      prevThanksFiltersRef.current = { thanksSelectedDate, thanksKeyword };
    }
  }, [thanksSelectedDate, thanksKeyword, activeTab, searchParams, navigate]);

  // コメント件数を取得する関数
  const fetchCommentCounts = useCallback(async (postIds: string[]) => {
    if (postIds.length === 0) return {};
    try {
      const counts = await PostService.getCommentCounts(postIds);
      return counts;
    } catch (error) {
      console.error('コメント件数取得エラー:', error);
      return {};
    }
  }, []);

  const fetchPosts = useCallback(async () => {
    if (!company?.id) return;
    setPostsLoading(true);
    try {
      let status: 'pending' | 'approved' | 'rejected' | undefined;
      if (activeTab === 'thanks') {
        status = undefined; // サンクスボックスは全て表示
      } else {
        status = 'pending'; // 従来挙動（voiceはリアルタイム側で取得）
      }
      const fetchedPosts = await PostService.getPosts(company.id, status);
      setPosts(fetchedPosts);
    } catch (error) {
      console.error('投稿取得エラー:', error);
    } finally {
      setPostsLoading(false);
    }
  }, [company?.id, activeTab]);

  useEffect(() => {
    if (!company?.id) return;

    if (activeTab === 'voice') {
      // voiceタブはリアルタイム購読
      setPostsLoading(true);
      const q = fsQuery(
        collection(db, 'posts'),
        where('companyId', '==', company.id),
        where('type', '==', 'voice'),
        orderBy('createdAt', 'desc'),
        fsLimit(200)
      );
      const unsub = onSnapshot(
        q,
        async (snap) => {
          const list: Post[] = snap.docs.map((d) => ({ ...(d.data() as Record<string, unknown>), id: d.id, postId: d.id })) as Post[];
          setVoicePosts(list);
          
          // コメント件数を取得
          const postIds = list.map(post => post.postId);
          const commentCounts = await fetchCommentCounts(postIds);
          setVoiceCommentCounts(commentCounts);
          
          setPostsLoading(false);
        },
        () => {
          setPostsLoading(false);
        }
      );
      return () => {
        unsub();
      };
    } else if (activeTab === 'thanks') {
      // thanksタブはリアルタイム購読
      setPostsLoading(true);
      const q = fsQuery(
        collection(db, 'posts'),
        where('companyId', '==', company.id),
        where('type', '==', 'thanks'),
        orderBy('createdAt', 'desc'),
        fsLimit(200)
      );
      const unsub = onSnapshot(
        q,
        (snap) => {
          const list: Post[] = snap.docs.map((d) => ({ ...(d.data() as Record<string, unknown>), id: d.id, postId: d.id })) as Post[];
          setThanksPosts(list);
          setPostsLoading(false);
        },
        () => setPostsLoading(false)
      );
      return () => {
        unsub();
      };
    } else {
      // その他タブは従来の取得
      fetchPosts();
    }
  }, [company?.id, activeTab, fetchPosts]);

  // フィルタリング（voiceタブのみ）
  const filteredVoicePosts = useMemo(() => {
    let list = [...voicePosts];
    // 日付フィルタ（年月日で一致）
    if (selectedDate) {
      const y = selectedDate.getFullYear();
      const m = selectedDate.getMonth();
      const d = selectedDate.getDate();
      list = list.filter((p) => {
        const created = toDate(p.createdAt);
        return created.getFullYear() === y && created.getMonth() === m && created.getDate() === d;
      });
    }
    // キーワード
    const kw = keyword.trim().toLowerCase();
    if (kw) {
      list = list.filter((p) => {
        const text = (p.processedContent || p.content || '').toLowerCase();
        const tags = Array.isArray(p.tags) ? p.tags : [];
        return text.includes(kw) || tags.some((t: unknown) => String(t).toLowerCase().includes(kw));
      });
    }
    // ステータス
    if (selectedStatus) {
      list = list.filter((p) => p.status === selectedStatus);
    }
    return list;
  }, [voicePosts, selectedDate, keyword, selectedStatus]);

  // ページネーション適用後のvoice投稿リスト
  const paginatedVoicePosts = useMemo(() => {
    if (activeTab !== 'voice') return [];
    const startIndex = (voiceCurrentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredVoicePosts.slice(startIndex, endIndex);
  }, [filteredVoicePosts, voiceCurrentPage, activeTab]);

  // voiceタブの総ページ数
  const voiceTotalPages = useMemo(() => {
    if (activeTab !== 'voice') return 0;
    return Math.ceil(filteredVoicePosts.length / ITEMS_PER_PAGE);
  }, [filteredVoicePosts.length, activeTab]);

  // フィルタリング（thanksタブのみ）
  const filteredThanksPosts = useMemo(() => {
    let list = [...thanksPosts];
    if (thanksSelectedDate) {
      const y = thanksSelectedDate.getFullYear();
      const m = thanksSelectedDate.getMonth();
      const d = thanksSelectedDate.getDate();
      list = list.filter((p) => {
        const created = toDate(p.createdAt);
        return created.getFullYear() === y && created.getMonth() === m && created.getDate() === d;
      });
    }
    const kw = thanksKeyword.trim().toLowerCase();
    if (kw) {
      list = list.filter((p) => {
        const text = (p.processedContent || p.content || '').toLowerCase();
        const tags = Array.isArray(p.tags) ? p.tags : [];
        return text.includes(kw) || tags.some((t: unknown) => String(t).toLowerCase().includes(kw));
      });
    }
    return list;
  }, [thanksPosts, thanksSelectedDate, thanksKeyword]);

  // ページネーション適用後のthanks投稿リスト
  const paginatedThanksPosts = useMemo(() => {
    if (activeTab !== 'thanks') return [];
    const startIndex = (thanksCurrentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredThanksPosts.slice(startIndex, endIndex);
  }, [filteredThanksPosts, thanksCurrentPage, activeTab]);

  // thanksタブの総ページ数
  const thanksTotalPages = useMemo(() => {
    if (activeTab !== 'thanks') return 0;
    return Math.ceil(filteredThanksPosts.length / ITEMS_PER_PAGE);
  }, [filteredThanksPosts.length, activeTab]);

  // ページネーション適用後のtheme投稿リスト
  const paginatedThemePosts = useMemo(() => {
    if (activeTab !== 'theme') return [];
    const startIndex = (themeCurrentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return posts.slice(startIndex, endIndex);
  }, [posts, themeCurrentPage, activeTab]);

  // themeタブの総ページ数
  const themeTotalPages = useMemo(() => {
    if (activeTab !== 'theme') return 0;
    return Math.ceil(posts.length / ITEMS_PER_PAGE);
  }, [posts.length, activeTab]);

  // 登録情報ダイアログを表示（sessionStorageから取得、会社登録後のみ）
  useEffect(() => {
    if (!authLoading && !companyLoading && user && company) {
      const storedInfo = sessionStorage.getItem('registrationInfo');
      if (storedInfo) {
        try {
          const info = JSON.parse(storedInfo);
          // 会社登録後のフラグがある場合のみ表示
          if (info.isNewRegistration) {
            setRegistrationInfo(info);
            setRegistrationInfoDialog(true);
            // sessionStorageから削除（一度だけ表示）
            sessionStorage.removeItem('registrationInfo');

            // 投稿用のQRコード・URLを用意して、そのままダイアログに表示する。
            // 会社管理を開いた時点でも作られるが、登録直後に済ませておくことで
            // 管理者は「発行する」という操作を意識せずに済む。
            ensureAccessLink()
              .then((links) => {
                setAccessLinkUrl(links.find((link) => !link.revokedAt)?.url ?? null);
              })
              .catch((error) => {
                console.error('[会社登録] QRコードの準備に失敗しました:', error);
              });
          }
        } catch (e) {
          console.error('登録情報の取得に失敗しました:', e);
        }
      }
    }
  }, [authLoading, companyLoading, user, company]);

  /** 登録完了の案内から、掲示用の画像を保存する */
  const handleRegistrationPosterSave = async () => {
    if (!accessLinkUrl) return;
    try {
      await saveAccessLinkPoster({
        elementId: 'registration-access-qr',
        fileName: `${registrationInfo?.corporateCode || 'company'}_投稿用QR.png`,
        companyName: company?.companyName ?? '',
        url: accessLinkUrl,
      });
      setAccessLinkNotice('掲示用の画像を保存しました');
    } catch (error) {
      console.error('掲示用画像の保存エラー:', error);
      setAccessLinkNotice('掲示用の画像の保存に失敗しました');
    }
  };

  /** 登録完了の案内から、投稿用のURLだけをコピーする */
  const handleRegistrationUrlCopy = async () => {
    if (!accessLinkUrl) return;
    try {
      await navigator.clipboard.writeText(accessLinkUrl);
      setAccessLinkNotice('URLをコピーしました');
    } catch (error) {
      console.error('URLのコピーエラー:', error);
      setAccessLinkNotice('URLのコピーに失敗しました');
    }
  };

  /** 登録完了の案内から、配布用の案内文をコピーする */
  const handleRegistrationMessageCopy = async () => {
    if (!accessLinkUrl) return;
    try {
      await navigator.clipboard.writeText(
        buildAccessLinkMessage(company?.companyName ?? '', accessLinkUrl)
      );
      setAccessLinkNotice('案内文をコピーしました');
    } catch (error) {
      console.error('案内文のコピーエラー:', error);
      setAccessLinkNotice('コピーに失敗しました');
    }
  };

  // ユーティリティ
  const toDate = (ts: unknown): Date => {
    if (!ts) return new Date(0);
    if (ts instanceof Date) return ts;
    if (typeof (ts as { toDate?: () => Date }).toDate === 'function') return (ts as { toDate: () => Date }).toDate();
    if (typeof (ts as { seconds?: number }).seconds === 'number') return new Date((ts as { seconds: number }).seconds * 1000);
    return new Date(ts as number);
  };

  const relativeTimeJa = (date: Date): string => {
    const diffMs = Date.now() - date.getTime();
    const s = Math.floor(diffMs / 1000);
    if (s < 60) return `${s}秒前`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}分前`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}時間前`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}日前`;
    return date.toLocaleDateString('ja-JP');
  };

  const voiceStatusColor = (status: string): string => {
    switch (status) {
      case 'accepted':
        return '#1976d2'; // blue
      case 'in_progress':
        return '#ed6c02'; // orange
      case 'completed':
        return '#2e7d32'; // green
      case 'pending':
      default:
        return '#9e9e9e'; // grey
    }
  };

  const voiceStatusLabel = (status: string): string => {
    switch (status) {
      case 'accepted':
        return '受理';
      case 'in_progress':
        return '対応中';
      case 'completed':
        return '完了';
      case 'pending':
      default:
        return '未処理';
    }
  };

  const voiceStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted':
        return <CheckCircleOutlineIcon sx={{ fontSize: 16, color: voiceStatusColor(status) }} />;
      case 'in_progress':
        return <WorkOutlineIcon sx={{ fontSize: 16, color: voiceStatusColor(status) }} />;
      case 'completed':
        return <CheckCircleIcon sx={{ fontSize: 16, color: voiceStatusColor(status) }} />;
      case 'pending':
      default:
        return <PendingIcon sx={{ fontSize: 16, color: voiceStatusColor(status) }} />;
    }
  };

  const toggleExpanded = (postId: string) => {
    setExpandedPostMap((prev) => ({ ...prev, [postId]: !prev[postId] }));
  };

  const openStatusChangeDialog = (post: Post) => setStatusChangeDialog({ open: true, post });
  const closeStatusChangeDialog = () => setStatusChangeDialog({ open: false, post: null });

  const handleChangeStatus = async (newStatus: 'pending' | 'accepted' | 'in_progress' | 'completed') => {
    if (!statusChangeDialog.post) return;
    const post = statusChangeDialog.post;
    try {
      // 変更前データ取得
      const postRef = doc(db, 'posts', post.postId);
      const snap = await getDoc(postRef);
      const data = (snap.data() as Record<string, unknown>) || {};
      const oldStatus: string = (data as { status?: string }).status || 'unknown';
      const postContent: string = (data as { content?: string }).content || '';

      // 更新
      const update: Record<string, unknown> = {
        status: newStatus,
        updatedAt: serverTimestamp(),
      };
      if (newStatus === 'completed') {
        update.completedAt = serverTimestamp();
        update.completedBy = user?.uid || null;
      }
      await updateDoc(postRef, update);

      // 監査ログ
      if (user?.uid && company?.id) {
        const voiceBoxName = getBoxTemplate(company?.industryType, 'voice').name;
        await addDoc(collection(db, 'audit_logs'), {
          action: 'voice',
          description: `${voiceBoxName}ステータス変更 [${voiceStatusLabel(oldStatus)}] → [${voiceStatusLabel(newStatus)}]`,
          userId: user.uid,
          userName: user.displayName || '管理者',
          companyId: company.id,
          timestamp: serverTimestamp(),
          details: {
            postId: post.postId,
            postType: 'voice',
            // プレビューは全文を保存（監査ログ表示側で短縮を制御）
            postContentPreview: postContent,
            oldStatus,
            newStatus,
            oldStatusLabel: voiceStatusLabel(oldStatus),
            newStatusLabel: voiceStatusLabel(newStatus),
            actionType: 'status_change',
          },
        });
      }
    } catch (e) {
      console.error('ステータス変更に失敗しました', e);
    } finally {
      closeStatusChangeDialog();
    }
  };

  // サンクス: 表示/非表示トグル
  const toggleThanksVisibility = async (post: Post) => {
    try {
      // ✅ 権限チェック：管理者以上（admin, superadmin）のみ実行可能に緩和
      const userType = user?.userType;
      const isAdminOrHigher = userType === 'admin' || userType === 'superadmin';
      if (!isAdminOrHigher) {
        console.error('表示設定の変更に失敗しました: 管理者権限が必要です', { userType });
        return;
      }

      // ✅ 会社チェック：同じ会社の投稿のみ変更可能
      if (post.companyId !== company?.id) {
        console.error('表示設定の変更に失敗しました: 他の会社の投稿は変更できません');
        return;
      }

      const postRef = doc(db, 'posts', post.postId);
      const snap = await getDoc(postRef);
      const data = (snap.data() as Record<string, unknown>) || {};
      const oldStatus: string = (data as { status?: string }).status || 'unknown';
      const isVisible = oldStatus === 'approved';
      const newStatus = isVisible ? 'pending' : 'approved';

      await updateDoc(postRef, {
        status: newStatus,
        updatedAt: serverTimestamp(),
        ...(newStatus === 'approved' ? { approvedAt: serverTimestamp(), approvedBy: user?.uid || null } : {}),
      });

      // 楽観的更新（スナップショット到着前に即時反映）
      setThanksPosts((curr) => curr.map((p) => (
        p.postId === post.postId ? { ...p, status: newStatus } as Post : p
      )));

      if (user?.uid && company?.id) {
        const thanksBoxName = getBoxTemplate(company?.industryType, 'thanks').name;
        await addDoc(collection(db, 'audit_logs'), {
          action: 'thanks',
          description: `${thanksBoxName}表示変更 [${isVisible ? '表示中' : '非表示'}] → [${newStatus === 'approved' ? '表示中' : '非表示'}]`,
          userId: user.uid,
          userName: user.displayName || '管理者',
          companyId: company.id,
          timestamp: serverTimestamp(),
          details: {
            postId: post.postId,
            postType: 'thanks',
            // プレビューは全文を保存（監査ログ表示側で短縮を制御）
            postContentPreview: String((data as { content?: string }).content || ''),
            oldStatus,
            newStatus,
            oldStatusLabel: isVisible ? '表示中' : '非表示',
            newStatusLabel: newStatus === 'approved' ? '表示中' : '非表示',
            actionType: 'visibility_change',
          },
        });
      }
    } catch (e) {
      console.error('表示設定の変更に失敗しました', e);
    }
  };

  const clearFilters = () => {
    setSelectedDate(null);
    setKeyword('');
    setSelectedStatus(null);
  };

  const clearThanksFilters = () => {
    setThanksSelectedDate(null);
    setThanksKeyword('');
    setThanksVisibilityFilter(null);
  };

  // 未使用: 旧フローのため保持
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSwitchToHome = async () => {
    
    // 切り替え中フラグを設定（ProtectedRouteでのリダイレクトを防ぐ）
    sessionStorage.setItem('switching_user', 'true');
    
    // ドロワーを閉じる
    setDrawerOpen(false);
    
    // 状態の更新を確実にするため少し待機
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // 切り替え状態を設定
    setIsSwitching(true);
    
    
    // UIが更新されるまで少し待機
    await new Promise(resolve => setTimeout(resolve, 300));
    
    try {
      
      // 管理者から一般ユーザーに切り替え
      await AuthService.switchToCorporateUser();
      
      
      // 切り替え完了後、少し待機
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 切り替え中フラグをクリア
      sessionStorage.removeItem('switching_user');
      
      // 切り替え完了フラグを設定（ホーム画面でスナックバーを表示するため）
      sessionStorage.setItem('user_switched', 'true');
      
      // ページ全体をリロードしてホーム画面へ
      window.location.href = '/home';
    } catch (error) {
      console.error('一般ユーザーへの切り替えエラー:', error);
      
      // エラーの場合も少し待機
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // フラグをクリア
      sessionStorage.removeItem('switching_user');
      
      // エラーが発生した場合でもホーム画面に移動を試行
      window.location.href = '/home';
    }
  };


  const getTabTitle = () => {
    return getBoxTemplate(company?.industryType, activeTab as 'voice' | 'thanks' | 'theme').name;
  };


  if (authLoading || companyLoading || isSwitching) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: AppColors.surface,
          position: 'relative',
        }}
      >
        <CircularProgress 
          size={60}
          thickness={4}
          sx={{ color: AppColors.primary }}
        />
        
        {isSwitching && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              bgcolor: '#424242',
              color: 'white',
              px: 2,
              py: 1,
              borderRadius: 1,
              boxShadow: '0 3px 5px -1px rgba(0,0,0,0.2), 0 6px 10px 0 rgba(0,0,0,0.14), 0 1px 18px 0 rgba(0,0,0,0.12)',
              width: 300,
              maxWidth: '90%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ fontSize: 14, color: 'white', lineHeight: 1.43 }}>
              一般ユーザーに切り替え中...
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: AppColors.surface, display: 'flex', flexDirection: 'column' }}>
      {/* ヘッダー */}
      <Header 
        onMenuClick={() => setDrawerOpen(true)}
        onSettingsClick={() => navigate('/settings')}
        showAdminBadge={true}
        freeTrialEndDate={company?.isFreeTrial ? company.freeTrialEndDate : undefined}
        companyName={company?.companyName}
        corporateCode={company?.corporateCode}
      />

      {/* モバイル用サイドバー */}
      {!isDesktop && (
        <AdminSidebar 
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onTabChange={(tab) => {
            setActiveTab(tab);
            // タブ切替時に従来のpostsも更新（thanks->voice等で空表示にならないよう）
            if (tab !== 'voice') {
              fetchPosts();
            }
          }}
        />
      )}

      {/* レイアウトコンテナ（ヘッダーの高さ分のpaddingを追加） */}
      <Box sx={{ display: 'flex', flex: 1, pt: '64px' }}>
        {/* PC版用固定サイドバー */}
        {isDesktop && (
          <AdminDesktopSidebar 
            onTabChange={(tab) => {
              setActiveTab(tab);
              if (tab !== 'voice') {
                fetchPosts();
              }
            }}
          />
        )}

        {/* メインコンテンツ */}
        <Box sx={{ 
          width: '100%', 
          maxWidth: '100%',
          marginLeft: isDesktop ? '240px' : 0,
          pb: isDesktop ? 0 : 10, // スマホでフッター分の下パディングを追加
        }}>
        {/* 中ヘッダー */}
        <Box sx={{ width: '100%', p: 2, bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '24px' }}>
            <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'black' }}>
              {getTabTitle()}
            </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '24px' }}>
              {activeTab === 'voice' && (
                <>
                  {(selectedDate || keyword || selectedStatus) && (
                    <Tooltip title="条件クリア">
                      <IconButton onClick={clearFilters} sx={{ color: 'black', p: 0.5, minWidth: 'auto' }}>
                        <ClearIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="ステータスフィルター">
                    <IconButton onClick={() => setStatusFilterOpen(true)} sx={{ color: selectedStatus ? '#1976d2' : 'black', p: 0.5, minWidth: 'auto' }}>
                      <FilterListIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="検索・フィルター">
                    <IconButton onClick={() => { setTempKeyword(keyword); setTempDate(selectedDate); setSearchDialogOpen(true); }} sx={{ color: 'black', p: 0.5, minWidth: 'auto' }}>
                      <SearchIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              {activeTab === 'thanks' && (
                <>
                  {(thanksSelectedDate || thanksKeyword || thanksVisibilityFilter) && (
                    <Tooltip title="条件クリア">
                      <IconButton onClick={clearThanksFilters} sx={{ color: 'black', p: 0.5, minWidth: 'auto' }}>
                        <ClearIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="承認済みのみ">
                    <IconButton onClick={() => setThanksVisibilityFilter(thanksVisibilityFilter === 'approved' ? null : 'approved')} sx={{ color: thanksVisibilityFilter === 'approved' ? 'green' : 'black', p: 0.5, minWidth: 'auto' }}>
                      <VisibilityIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="非表示のみ">
                    <IconButton onClick={() => setThanksVisibilityFilter(thanksVisibilityFilter === 'pending' ? null : 'pending')} sx={{ color: thanksVisibilityFilter === 'pending' ? '#ed6c02' : 'black', p: 0.5, minWidth: 'auto' }}>
                      <VisibilityOffIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="検索・フィルター">
                    <IconButton onClick={() => { setThanksTempKeyword(thanksKeyword); setThanksTempDate(thanksSelectedDate); setThanksSearchDialogOpen(true); }} sx={{ color: 'black', p: 0.5, minWidth: 'auto' }}>
                      <SearchIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
            </>
          )}
            </Box>
          </Box>
        </Box>

        {/* 投稿一覧 */}
        <Box sx={{ 
          flex: 1, 
          p: 2,
          maxWidth: isDesktop ? '1000px' : '100%',
          mx: isDesktop ? 'auto' : 0,
          width: '100%',
        }}>
          {postsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (activeTab === 'voice'
                ? filteredVoicePosts.length === 0
                : activeTab === 'thanks'
                  ? filteredThanksPosts.length === 0
                  : posts.length === 0) ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
                textAlign: 'center',
                minHeight: '50vh',
              }}
            >
              <InboxIcon sx={{ fontSize: 80, color: '#e0e0e0', mb: 3 }} />
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 500,
                  color: AppColors.textSecondary,
                  mb: 1,
                }}
              >
                投稿がありません
              </Typography>
            </Box>
          ) : (
            <Box>
              {(activeTab === 'voice' ? paginatedVoicePosts : activeTab === 'thanks' ? paginatedThanksPosts : paginatedThemePosts).map((post) => {
                if (activeTab !== 'voice') {
                  if (activeTab === 'thanks') {
                    // サンクスカード（表示フィルタをカード側で適用）
                    const hideByFilter = (thanksVisibilityFilter === 'approved' && post.status !== 'approved') || (thanksVisibilityFilter === 'pending' && post.status === 'approved');
                    if (hideByFilter) return null;

                    const isExpanded = !!thanksExpandedMap[post.postId];
                    const content = post.processedContent || post.content || '';
                    const needsExpansion = !!thanksClampedMap[post.postId];
                    const created = toDate(post.createdAt);
                    const isVisible = post.status === 'approved';
                    const borderColor = isVisible ? 'green' : 'red';
                    const bgColor = isVisible ? 'rgba(76,175,80,0.1)' : 'rgba(244,67,54,0.1)';

                    return (
                      <Card 
                        key={post.id} 
                        sx={{ mb: 2, boxShadow: 2 }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                sx={{
                                px: 1,
                                py: 0.5,
                                borderRadius: 1.5,
                                bgcolor: AppColors.thanksBoxColor + '20',
                                color: AppColors.thanksBoxColor,
                                fontSize: 12,
                                fontWeight: 700,
                              }}
                            >
                              {getBoxTemplate(company?.industryType, 'thanks').name}
                            </Box>
                            <Box sx={{ flex: 1 }} />
                            {/* 表示/非表示トグル（管理者以上） */}
                            {(user?.userType === 'admin' || user?.userType === 'superadmin') && (
                              <Box
                                role="button"
                                tabIndex={0}
                                onClick={(e) => { e.stopPropagation(); toggleThanksVisibility(post); }}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleThanksVisibility(post); } }}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 1.5,
                    py: 0.5,
                                  borderRadius: 1.5,
                                  border: `1px solid ${borderColor}`,
                                  bgcolor: bgColor,
                                  cursor: 'pointer',
                                }}
                              >
                                {isVisible ? (
                                  <VisibilityIcon sx={{ fontSize: 16, color: 'green' }} />
                                ) : (
                                  <VisibilityOffIcon sx={{ fontSize: 16, color: 'red' }} />
                                )}
                                <Typography sx={{ ml: 0.5, fontSize: 12, fontWeight: 700, color: isVisible ? 'green' : 'red' }}>
                                  {isVisible ? '表示中' : '非表示'}
                  </Typography>
            </Box>
                            )}
        </Box>

                          <Typography
                            sx={{ fontSize: 14, lineHeight: 1.6, color: AppColors.textPrimary, mt: 1, whiteSpace: 'pre-line' }}
                            style={{
                              display: isDesktop ? 'block' : '-webkit-box',
                              WebkitLineClamp: isDesktop ? 'none' : (isExpanded ? 'none' : 3),
                              WebkitBoxOrient: 'vertical',
                              overflow: isDesktop ? 'visible' : 'hidden',
                            }}
                            ref={(el) => {
                              if (!el) return;
                              const clamped = el.scrollHeight > el.clientHeight + 1;
                              if (thanksClampedMap[post.postId] !== clamped) {
                                setThanksClampedMap((prev) => ({ ...prev, [post.postId]: clamped }));
                              }
                            }}
                          >
                            {content}
                          </Typography>
                          {needsExpansion && !isDesktop && (
                            <Box sx={{ mt: 0.5 }}>
                    <Typography
                                onClick={() => setThanksExpandedMap((prev) => ({ ...prev, [post.postId]: !isExpanded }))}
                                sx={{ color: AppColors.primary, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-block' }}
                              >
                                {isExpanded ? '表示を減らす' : 'さらに表示'}
                </Typography>
                            </Box>
                          )}

                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            {Array.isArray(post.tags) && post.tags.length > 0 && (
                              <Typography sx={{ fontSize: 12, color: AppColors.textSecondary, mr: 1 }}>
                                #{post.tags.join(', ')}
                              </Typography>
                            )}
                            <Box sx={{ flex: 1 }} />
                            <Typography sx={{ fontSize: 12, color: AppColors.textSecondary }}>
                              {relativeTimeJa(created)}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  }
                  // 既存表示（非voice/thanks）
                  return (
                    <Card key={post.id} sx={{ mb: 2, boxShadow: 2 }}>
                      <CardContent sx={{ p: 2 }}>
                        <Typography sx={{ fontSize: 14, lineHeight: 1.5, color: AppColors.textPrimary, mb: 1, whiteSpace: 'pre-line' }}>
                          {post.content}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: '#666' }}>
                          {post.createdAt ? toDate(post.createdAt).toLocaleDateString('ja-JP') : ''}
                        </Typography>
                      </CardContent>
                    </Card>
                  );
                }

                const isExpanded = !!expandedPostMap[post.postId];
                const content = post.processedContent || post.content || '';
                const needsExpansion = !!voiceClampedMap[post.postId];
                const created = toDate(post.createdAt);

                return (
                  <Card 
                    key={post.id} 
                    sx={{ mb: 2, boxShadow: 2, cursor: 'pointer' }}
                    onClick={() => {
                      const params = new URLSearchParams();
                      params.set('tab', 'voice');
                      if (voiceCurrentPage > 1) {
                        params.set('voicePage', voiceCurrentPage.toString());
                      }
                      navigate(`/admin/voice/${post.postId}?${params.toString()}`);
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderRadius: 1.5,
                            bgcolor: AppColors.primary + '20',
                            color: AppColors.primary,
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                      {getBoxTemplate(company?.industryType, 'voice').name}
                        </Box>
                        <Box sx={{ flex: 1 }} />
                        {/* ステータス選択 */}
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            openStatusChangeDialog(post);
                          }}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1.5,
                            border: `1px solid ${voiceStatusColor(post.status)}`,
                            bgcolor: voiceStatusColor(post.status) + '20',
                            cursor: 'pointer',
                          }}
                        >
                          {voiceStatusIcon(post.status)}
                          <Typography
                            sx={{
                              ml: 0.5,
                              fontSize: 12,
                              fontWeight: 700,
                              color: voiceStatusColor(post.status),
                            }}
                          >
                            {voiceStatusLabel(post.status)}
                          </Typography>
                        </Box>
                      </Box>

                      <Typography
                        sx={{ fontSize: 14, lineHeight: 1.6, color: AppColors.textPrimary, mt: 1, whiteSpace: 'pre-line' }}
                        style={{
                          display: isDesktop ? 'block' : '-webkit-box',
                          WebkitLineClamp: isDesktop ? 'none' : (isExpanded ? 'none' : 3),
                          WebkitBoxOrient: 'vertical',
                          overflow: isDesktop ? 'visible' : 'hidden',
                        }}
                        ref={(el) => {
                          if (!el) return;
                          const clamped = el.scrollHeight > el.clientHeight + 1;
                          if (voiceClampedMap[post.postId] !== clamped) {
                            setVoiceClampedMap((prev) => ({ ...prev, [post.postId]: clamped }));
                          }
                        }}
                      >
                        {content}
                    </Typography>
                      {needsExpansion && !isDesktop && (
                        <Box sx={{ mt: 0.5 }}>
                    <Typography
                            onClick={() => toggleExpanded(post.postId)}
                            sx={{ color: AppColors.primary, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-block' }}
                          >
                            {isExpanded ? '表示を減らす' : 'さらに表示'}
                    </Typography>
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        {Array.isArray(post.tags) && post.tags.length > 0 && (
                          <Typography sx={{ fontSize: 12, color: AppColors.textSecondary }}>
                            #{post.tags.join(', ')}
                          </Typography>
                        )}
                      </Box>

                      {/* コメント件数表示 */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ChatBubbleOutlineIcon sx={{ fontSize: 16, color: AppColors.textSecondary, mr: 0.5 }} />
                          <Typography sx={{ fontSize: 12, color: AppColors.textSecondary, fontWeight: 500 }}>
                            {voiceCommentCounts[post.postId] || 0}件
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }} />
                        <Typography sx={{ fontSize: 12, color: AppColors.textSecondary }}>
                          {created.toLocaleDateString('ja-JP')}
                        </Typography>
                      </Box>
                  </CardContent>
                </Card>
                );
              })}
              
              {/* voiceタブのページネーション */}
              {activeTab === 'voice' && voiceTotalPages > 1 && (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: 2,
                  p: 3,
                  pt: 2,
                }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: AppColors.textSecondary,
                      mb: 1,
                    }}
                  >
                    {filteredVoicePosts.length}件中 {((voiceCurrentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(voiceCurrentPage * ITEMS_PER_PAGE, filteredVoicePosts.length)}件を表示
                  </Typography>
                  <Pagination
                    currentPage={voiceCurrentPage}
                    totalPages={voiceTotalPages}
                    onPageChange={setVoiceCurrentPage}
                    disabled={postsLoading}
                  />
                </Box>
              )}

              {/* thanksタブのページネーション */}
              {activeTab === 'thanks' && thanksTotalPages > 1 && (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: 2,
                  p: 3,
                  pt: 2,
                }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: AppColors.textSecondary,
                      mb: 1,
                    }}
                  >
                    {filteredThanksPosts.length}件中 {((thanksCurrentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(thanksCurrentPage * ITEMS_PER_PAGE, filteredThanksPosts.length)}件を表示
                  </Typography>
                  <Pagination
                    currentPage={thanksCurrentPage}
                    totalPages={thanksTotalPages}
                    onPageChange={setThanksCurrentPage}
                    disabled={postsLoading}
                  />
                </Box>
              )}

              {/* themeタブのページネーション */}
              {activeTab === 'theme' && themeTotalPages > 1 && (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  gap: 2,
                  p: 3,
                  pt: 2,
                }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: AppColors.textSecondary,
                      mb: 1,
                    }}
                  >
                    {posts.length}件中 {((themeCurrentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(themeCurrentPage * ITEMS_PER_PAGE, posts.length)}件を表示
                  </Typography>
                  <Pagination
                    currentPage={themeCurrentPage}
                    totalPages={themeTotalPages}
                    onPageChange={setThemeCurrentPage}
                    disabled={postsLoading}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
        </Box>
      </Box>

      {/* モバイル版のみボトムナビゲーション表示 */}
      {!isDesktop && (
        <AdminBottomNavigation 
          currentTab={activeTab as 'voice' | 'thanks' | 'theme'}
          onTabChange={(tab) => {
            if (tab === 'theme') {
              navigate('/admin/theme-posts');
            } else {
              setActiveTab(tab);
            }
          }}
        />
      )}

      {/* voiceタブ用ダイアログ */}
      <StatusChangeDialog
        open={statusChangeDialog.open}
        onClose={closeStatusChangeDialog}
        onSelect={handleChangeStatus}
      />
      <StatusFilterDialog
        open={statusFilterOpen}
        selected={selectedStatus}
        onSelect={(s) => { setSelectedStatus(s); setStatusFilterOpen(false); }}
        onClose={() => setStatusFilterOpen(false)}
      />
      <SearchFilterDialog
        open={searchDialogOpen}
        keyword={tempKeyword}
        date={tempDate}
        onKeywordChange={setTempKeyword}
        onDateChange={setTempDate}
        onApply={() => { setKeyword(tempKeyword); setSelectedDate(tempDate); setSearchDialogOpen(false); }}
        onClose={() => setSearchDialogOpen(false)}
      />
      {/* thanks用 検索ダイアログ */}
      <SearchFilterDialog
        open={thanksSearchDialogOpen}
        keyword={thanksTempKeyword}
        date={thanksTempDate}
        onKeywordChange={setThanksTempKeyword}
        onDateChange={setThanksTempDate}
        onApply={() => { setThanksKeyword(thanksTempKeyword); setThanksSelectedDate(thanksTempDate); setThanksSearchDialogOpen(false); }}
        onClose={() => setThanksSearchDialogOpen(false)}
      />
      
      {/* 登録情報ダイアログ */}
      <Dialog
        open={registrationInfoDialog}
        onClose={() => setRegistrationInfoDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>登録情報のご案内</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            管理画面にログインするときに会社IDが必要です。控えを保存してください。<br />
            メールアドレスとパスワードは、登録時にご入力いただいたものをお使いください。
          </Typography>
          {registrationInfo && (
            <Box>
              <Typography variant="caption" sx={{ color: AppColors.textSecondary }}>会社ID</Typography>
              <Paper variant="outlined" sx={{ p: 1.25 }}>{registrationInfo.corporateCode}</Paper>
            </Box>
          )}

          {/* 無料実証の期間。登録直後に一度お伝えする。
              期間中は管理画面に常時表示される（#20）。 */}
          {registrationInfo?.freeTrialEndDate && (
            <Paper
              variant="outlined"
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: `${AppColors.primary}14`,
                borderColor: AppColors.primary,
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" sx={{ color: AppColors.textSecondary }}>
                無料でご利用いただける期間
              </Typography>
              <Typography
                sx={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: AppColors.primaryDark,
                  lineHeight: 1.3,
                  mt: 0.5,
                }}
              >
                {new Date(registrationInfo.freeTrialEndDate).toLocaleDateString('ja-JP', {
                  year: 'numeric', month: 'long', day: 'numeric',
                })}まで
              </Typography>
              <Typography variant="caption" sx={{ color: AppColors.textSecondary, display: 'block', mt: 1 }}>
                期間中はすべての機能をお使いいただけます。<br />
                継続される場合は、会社管理からお支払い情報をご登録ください。
              </Typography>
            </Paper>
          )}

          {/*
            社員の方に配る投稿用のQRコード・URL。
            登録が終わった時点で自動的に用意されるため、発行の操作は不要。
          */}
          <Divider sx={{ my: 2.5 }} />
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            社員の方には、こちらのQRコード・URLを配ってください。読み取るだけで投稿できます。
          </Typography>
          {accessLinkUrl ? (
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: 'white',
                  borderRadius: 3,
                  border: `1px solid ${AppColors.border}`,
                  display: 'inline-block',
                  mb: 1,
                }}
              >
                <QRCodeSVG
                  id="registration-access-qr"
                  value={accessLinkUrl}
                  size={180}
                  level="M"
                  includeMargin={false}
                />
              </Box>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.25,
                  mb: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  textAlign: 'left',
                }}
              >
                <Box sx={{ flex: 1, wordBreak: 'break-all', fontSize: 13 }}>{accessLinkUrl}</Box>
                <Tooltip title="URLをコピー">
                  <IconButton
                    size="small"
                    onClick={handleRegistrationUrlCopy}
                    aria-label="URLをコピー"
                    sx={{ color: AppColors.primary }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Paper>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mb: 1.5 }}>
                <Button variant="contained" onClick={handleRegistrationPosterSave}>
                  掲示用の画像を保存
                </Button>
                <Button variant="outlined" onClick={handleRegistrationMessageCopy}>
                  案内文をコピー
                </Button>
              </Box>
              <Typography variant="caption" sx={{ color: AppColors.textSecondary }}>
                あとから見るときは、メニューの「QRコード・URL」から開けます
              </Typography>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setRegistrationInfoDialog(false)}
            variant="contained"
            sx={{
              backgroundColor: AppColors.primary,
              color: 'white',
              '&:hover': {
                backgroundColor: AppColors.primaryDark,
              },
            }}
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(accessLinkNotice)}
        autoHideDuration={3000}
        onClose={() => setAccessLinkNotice(null)}
        message={accessLinkNotice}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

// ここからダイアログ群（voiceタブ用）
// ステータス変更ダイアログ
const StatusChangeDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSelect: (status: 'pending' | 'accepted' | 'in_progress' | 'completed') => void;
}> = ({ open, onClose, onSelect }) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>ステータス変更</DialogTitle>
      <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 260 }}>
            <ListItemButton onClick={() => onSelect('pending')}>
              <ListItemIcon>
                <PendingIcon sx={{ color: '#9e9e9e' }} />
              </ListItemIcon>
              <ListItemText primary="未処理" primaryTypographyProps={{ sx: { color: '#9e9e9e', fontWeight: 600 } }} />
            </ListItemButton>
            <ListItemButton onClick={() => onSelect('accepted')}>
              <ListItemIcon>
                <CheckCircleOutlineIcon sx={{ color: '#1976d2' }} />
              </ListItemIcon>
              <ListItemText primary="受理" primaryTypographyProps={{ sx: { color: '#1976d2', fontWeight: 600 } }} />
            </ListItemButton>
            <ListItemButton onClick={() => onSelect('in_progress')}>
              <ListItemIcon>
                <WorkOutlineIcon sx={{ color: '#ed6c02' }} />
              </ListItemIcon>
              <ListItemText primary="対応中" primaryTypographyProps={{ sx: { color: '#ed6c02', fontWeight: 600 } }} />
            </ListItemButton>
            <ListItemButton onClick={() => onSelect('completed')}>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#2e7d32' }} />
              </ListItemIcon>
              <ListItemText primary="完了" primaryTypographyProps={{ sx: { color: '#2e7d32', fontWeight: 600 } }} />
            </ListItemButton>
          </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>キャンセル</Button>
      </DialogActions>
    </Dialog>
  );
};

// ステータスフィルタダイアログ
const StatusFilterDialog: React.FC<{
  open: boolean;
  selected: 'accepted' | 'in_progress' | 'completed' | 'pending' | null;
  onSelect: (s: 'accepted' | 'in_progress' | 'completed' | 'pending' | null) => void;
  onClose: () => void;
}> = ({ open, selected, onSelect, onClose }) => {
  const items: Array<{ key: null | 'pending' | 'accepted' | 'in_progress' | 'completed'; label: string; icon: React.ReactNode; color: string }> = [
    { key: null, label: 'すべて', icon: <CheckCircleIcon />, color: '#000000' },
    { key: 'pending', label: '未処理', icon: <PendingIcon />, color: '#9e9e9e' },
    { key: 'accepted', label: '受理', icon: <CheckCircleOutlineIcon />, color: '#1976d2' },
    { key: 'in_progress', label: '対応中', icon: <WorkOutlineIcon />, color: '#ed6c02' },
    { key: 'completed', label: '完了', icon: <CheckCircleIcon />, color: '#2e7d32' },
  ];
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>ステータスフィルター</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 260 }}>
          {items.map((it) => (
            <ListItemButton key={String(it.key)} onClick={() => onSelect(it.key)} selected={selected === it.key}>
              <ListItemIcon sx={{ color: it.color }}>{it.icon}</ListItemIcon>
              <ListItemText primary={it.label} primaryTypographyProps={{ sx: { color: it.color, fontWeight: 600 } }} />
            </ListItemButton>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>キャンセル</Button>
      </DialogActions>
    </Dialog>
  );
};

// 検索・日付フィルタダイアログ
const SearchFilterDialog: React.FC<{
  open: boolean;
  keyword: string;
  date: Date | null;
  onKeywordChange: (v: string) => void;
  onDateChange: (d: Date | null) => void;
  onApply: () => void;
  onClose: () => void;
}> = ({ open, keyword, date, onKeywordChange, onDateChange, onApply, onClose }) => {
  const isDesktop = useIsDesktop();
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: isDesktop ? '400px' : '90vw',
          margin: isDesktop ? '32px' : '16px',
        }
      }}
    >
      <DialogTitle>検索・フィルター</DialogTitle>
      <DialogContent>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 2, 
          width: '100%',
          minWidth: 0, // 横スクロールを防ぐ
        }}>
          <TextField 
            label="キーワード" 
            value={keyword} 
            onChange={(e) => onKeywordChange(e.target.value)} 
            size="small" 
            fullWidth
          />
          <TextField
            label="日付 (YYYY-MM-DD)"
            value={date ? date.toISOString().slice(0, 10) : ''}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) return onDateChange(null);
              const dt = new Date(v);
              if (!isNaN(dt.getTime())) onDateChange(dt);
            }}
            size="small"
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>キャンセル</Button>
        <Button onClick={onApply}>適用</Button>
      </DialogActions>
    </Dialog>
  );
};

// 既存のAdminPageをデフォルトエクスポートに変更（外部からは従来通り使用）
export default function AdminPageWithDialogs() {
  // 内部でAdminPageをレンダリングし、ダイアログをPortalで制御する場合はここで行うが
  // 今回はAdminPage自体に状態を持たせているため、そのまま返す
  return <AdminPage />;
}
